import { useEffect, useRef, useState } from 'react';
import type { ViewStyle } from 'react-native';
import { useAnimatedStyle, useSharedValue, withTiming, type AnimatedStyle } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import Dimens from 'src/core/utils/Dimens';

const FADE_OUT = { duration: Dimens.animationDuration100 };
const FADE_IN = { duration: Dimens.animationDuration250 };

// Swapping content in a single commit reads as a jump. Holding the swap until the old content has
// faded out hides the layout change that comes with it, and the new content fades in over the gap.
export function useCrossFade(key: string): { displayedKey: string; style: AnimatedStyle<ViewStyle> } {
	const opacity = useSharedValue(Dimens.opacity100);
	const [displayedKey, setDisplayedKey] = useState(key);
	const hasSwapped = useRef(false);

	useEffect(() => {
		if (key === displayedKey) {
			return;
		}

		opacity.value = withTiming(Dimens.opacity0, FADE_OUT, (finished) => {
			if (finished === true) {
				scheduleOnRN(setDisplayedKey, key);
			}
		});
	}, [displayedKey, key, opacity]);

	useEffect(() => {
		if (!hasSwapped.current) {
			hasSwapped.current = true;

			return;
		}

		opacity.value = withTiming(Dimens.opacity100, FADE_IN);
	}, [displayedKey, opacity]);

	const style = useAnimatedStyle(() => ({ opacity: opacity.value }), [opacity]);

	return { displayedKey, style };
}

export default useCrossFade;
